/**
 * AttackPathResidualPhase — Phase 3.
 *
 * Read-only view of the residual attack-path feasibility deltas the
 * agent computed in the `attack_path_analysis` phase. Each path
 * card shows previous → current feasibility rating with an optional
 * parameter breakdown (5 ISO 21434 attack-potential dimensions:
 * elapsed time, expertise, knowledge of item, window of opportunity,
 * equipment).
 *
 * No user action here — the backend auto-advances to risk_analysis
 * once the agent finishes. The rail click lets the user revisit
 * this surface after the workflow has moved on; in that case the
 * stop is purely historical.
 *
 * While the backend is still in `attack_path_analysis` the agent is
 * mid-run and there's no data to show yet — render a spinner-only
 * card. Polling in the hook will refresh data once the phase
 * transitions.
 */
import React, { useMemo } from 'react';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '../../../../../components/ui/Empty';
import { LoadingState } from '../../../../../components/ui/LoadingState';
import { TypographyH4, TypographyMuted } from '../../../../../components/ui/Typography';
import LevelBadgeComponent from '../../../../../components/ui/LevelBadge';
import AnalysisErrorCard from '../../AnalysisErrorCard/AnalysisErrorCard';
import { getFeasibilityColor } from '../../../../../utils/severityStyle';
import type { ResidualActions } from '../../../hooks/useResidualProgress';
import type { ResidualAttackPath, ResidualState } from '../../../state/types';
import styles from './AttackPathResidualPhase.module.css';

interface AttackPathResidualPhaseProps {
  state: ResidualState;
  actions: ResidualActions;
}

/* LevelBadge is an untyped JS primitive — `tsc` infers its default-less
 * props (color / textColor) as required. Narrow to the optional-prop
 * shape we actually use, matching its JS runtime. */
const LevelBadge = LevelBadgeComponent as unknown as React.FC<{
  color?: string;
  textColor?: string;
  label: React.ReactNode;
  size?: 'sm' | 'md';
  className?: string;
}>;

// LoadingState is a JSX primitive; cast for use from TSX (mirrors the
// ButtonAny convention).
const LoadingStateAny = LoadingState as unknown as React.ComponentType<any>;

/* The backend uses several field-name variants for the feasibility
 * rating + total score across phases. Accept any of them and the
 * loose types defined in `state/types.ts`. */
const pickRating = (
  ap: ResidualAttackPath,
  prefix: 'previous' | 'current',
): string | undefined =>
  (ap as Record<string, unknown>)[`${prefix}_attack_feasibility_rating`] as
    | string
    | undefined ??
  (ap as Record<string, unknown>)[`${prefix}_feasibility_rating`] as
    | string
    | undefined;

const pickScore = (
  ap: ResidualAttackPath,
  prefix: 'previous' | 'current',
): number | undefined => {
  const r = ap as Record<string, unknown>;
  const candidates = [
    r[`${prefix}_total_attack_potential`],
    r[`${prefix}_feasibility_score`],
  ];
  for (const v of candidates) {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return undefined;
};

/* Lower attack potential = harder to attack = improved (more
 * residual security after controls). Returns 'improved' / 'worsened'
 * / 'unchanged'. */
const compareScore = (
  prev: number | undefined,
  curr: number | undefined,
): 'improved' | 'worsened' | 'unchanged' => {
  if (prev == null || curr == null) return 'unchanged';
  if (curr > prev) return 'improved';
  if (curr < prev) return 'worsened';
  return 'unchanged';
};

const PARAM_KEYS: Array<{
  label: string;
  prevName: string;
  prevValue: string;
  currName: string;
  currValue: string;
}> = [
  {
    label: 'Elapsed Time',
    prevName: 'previous_elapsed_time',
    prevValue: 'previous_elapsed_time_value',
    currName: 'current_elapsed_time',
    currValue: 'current_elapsed_time_value',
  },
  {
    label: 'Specialist Expertise',
    prevName: 'previous_specialist_expertise',
    prevValue: 'previous_expertise_value',
    currName: 'current_specialist_expertise',
    currValue: 'current_expertise_value',
  },
  {
    label: 'Knowledge of Item',
    prevName: 'previous_knowledge_of_item',
    prevValue: 'previous_knowledge_value',
    currName: 'current_knowledge_of_item',
    currValue: 'current_knowledge_value',
  },
  {
    label: 'Window of Opportunity',
    prevName: 'previous_window_of_opportunity',
    prevValue: 'previous_window_value',
    currName: 'current_window_of_opportunity',
    currValue: 'current_window_value',
  },
  {
    label: 'Equipment',
    prevName: 'previous_equipment',
    prevValue: 'previous_equipment_value',
    currName: 'current_equipment',
    currValue: 'current_equipment_value',
  },
];

export default function AttackPathResidualPhase({
  state,
  actions,
}: AttackPathResidualPhaseProps) {
  const attackPaths = state.data?.attack_paths ?? [];
  const isAnalysing = state.backendPhase === 'attack_path_analysis';

  const stats = useMemo(() => {
    let total = 0;
    let improved = 0;
    let worsened = 0;
    for (const ap of attackPaths) {
      total += 1;
      const cmp = compareScore(pickScore(ap, 'previous'), pickScore(ap, 'current'));
      if (cmp === 'improved') improved += 1;
      else if (cmp === 'worsened') worsened += 1;
    }
    return { total, improved, worsened };
  }, [attackPaths]);

  return (
    <div className={styles.phase}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <TypographyH4>Residual Attack Paths</TypographyH4>
          <TypographyMuted className={styles.subtitle}>
            How each attack path&rsquo;s feasibility shifted once the agent
            took the implemented controls and requirement verdicts into
            account. Read-only view&mdash;risk-level deltas land on the
            next stop.
          </TypographyMuted>
        </div>
        <div className={styles.statsBlock}>
          <span className={styles.statsItem}>
            <strong>{stats.improved}</strong> Harder
          </span>
          <span className={styles.statsItem}>
            <strong>{stats.worsened}</strong> Easier
          </span>
          <span className={styles.statsTotal}>
            of {stats.total} path{stats.total === 1 ? '' : 's'}
          </span>
        </div>
      </header>

      {isAnalysing ? (
        state.analysisError ? (
          <AnalysisErrorCard
            title="Attack feasibility recompute failed"
            message={state.analysisError}
            onRetry={actions.continueWorkflow}
          />
        ) : (
          <LoadingStateAny
            variant="page"
            title="Recomputing attack feasibility&hellip;"
            description="The agent is rescoring each attack path against the updated controls and requirement evidence. This usually finishes in under a minute."
          />
        )
      ) : attackPaths.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No attack-path data yet</EmptyTitle>
            <EmptyDescription>
              No attack-path data available yet for this session.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className={styles.cardList}>
          {attackPaths.map((ap) => (
            <AttackPathCard key={ap.attack_path_id} attackPath={ap} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Single attack-path card.
   ============================================================ */
function AttackPathCard({ attackPath }: { attackPath: ResidualAttackPath }) {
  const prevRating = pickRating(attackPath, 'previous');
  const currRating = pickRating(attackPath, 'current');
  const prevScore = pickScore(attackPath, 'previous');
  const currScore = pickScore(attackPath, 'current');

  const r = attackPath as Record<string, unknown>;
  const hasAnyParam = PARAM_KEYS.some(
    (p) => r[p.prevName] != null || r[p.currName] != null,
  );
  const rationale =
    (r.rationale as string | undefined) ??
    (r.feasibility_rationale as string | undefined);

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <span className={styles.idChip}>{attackPath.attack_path_id}</span>
        <span className={styles.cardTitle}>
          {attackPath.threat_name || attackPath.threat_id || 'Attack path'}
        </span>
        {(attackPath.asset_name || attackPath.asset_id) && (
          <span className={styles.assetTag}>
            on {attackPath.asset_name || attackPath.asset_id}
          </span>
        )}
      </header>

      <div className={styles.deltaRow}>
        <div className={styles.deltaSide}>
          <span className={styles.deltaSideLabel}>Before</span>
          {prevRating ? (
            <LevelBadge size="sm" color={getFeasibilityColor(prevRating)} label={prevRating} />
          ) : (
            <span className={styles.mutedValue}>—</span>
          )}
        </div>
        <span className={styles.deltaArrow}>→</span>
        <div className={styles.deltaSide}>
          <span className={styles.deltaSideLabel}>After</span>
          {currRating ? (
            <LevelBadge size="sm" color={getFeasibilityColor(currRating)} label={currRating} />
          ) : (
            <span className={styles.mutedValue}>Pending</span>
          )}
        </div>
        {prevScore != null && currScore != null && (
          <span className={styles.scoreNote}>
            Score: {prevScore} → {currScore}
          </span>
        )}
      </div>

      {rationale && (
        <div className={styles.rationale}>
          <span className={styles.rationaleLabel}>Agent rationale:</span>{' '}
          {rationale}
        </div>
      )}

      {hasAnyParam && (
        <div className={styles.paramTable}>
          <span className={styles.paramHeading}>Parameter</span>
          <span className={styles.paramHeading}>Before</span>
          <span className={styles.paramHeading}></span>
          <span className={styles.paramHeading}>After</span>
          {PARAM_KEYS.map((p) => {
            const prev = r[p.prevName] as string | undefined;
            const prevV = r[p.prevValue] as number | undefined;
            const curr = r[p.currName] as string | undefined;
            const currV = r[p.currValue] as number | undefined;
            if (prev == null && curr == null) return null;
            const paramDir = compareScore(prevV, currV);
            const afterClass =
              paramDir === 'improved'
                ? `${styles.paramAfter} ${styles.paramAfterImproved}`
                : paramDir === 'worsened'
                ? `${styles.paramAfter} ${styles.paramAfterWorsened}`
                : styles.paramAfter;
            return (
              <React.Fragment key={p.label}>
                <span className={styles.paramLabel}>{p.label}</span>
                <span className={styles.paramBefore}>
                  {prev ?? '—'}
                  {prevV != null ? ` (${prevV})` : ''}
                </span>
                <span className={styles.paramArrow}>→</span>
                <span className={afterClass}>
                  {curr ?? '—'}
                  {currV != null ? ` (${currV})` : ''}
                </span>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </section>
  );
}
